<template>
    <!-- Sidebar -->
    <aside :class="expanded ? 'tw:w-70' : 'tw:w-20'"
        class="tw:h-screen tw:bg-slate-900 tw:text-slate-300 tw:flex tw:flex-col tw:fixed tw:top-0 tw:left-0 tw:z-40 tw:border-r tw:border-slate-800 tw:shadow-xl tw:select-none tw:transition-[width] tw:duration-300"
        @mouseenter="setSidebarHovering(true)" @mouseleave="setSidebarHovering(false)">

        <!-- Sidebar Brand Logo -->
        <div :class="expanded ? 'tw:h-40 tw:px-5' : 'tw:h-20 tw:px-2'"
            class="tw:flex tw:items-center tw:gap-3 tw:border-b tw:border-slate-800/80 tw:bg-slate-900/50 tw:transition-all tw:duration-300">
            <router-link to="/admin/dashboard"
                class="tw:flex tw:justify-center tw:flex-col tw:items-center tw:gap-3 tw:no-underline tw:w-full">
                <div :class="expanded ? 'tw:w-24 tw:h-24' : 'tw:w-10 tw:h-10'"
                    class="tw:rounded-xl tw:bg-brand-primary/20 tw:border tw:border-brand-primary/30 tw:flex tw:items-center tw:justify-center tw:overflow-hidden tw:shrink-0 tw:transition-all tw:duration-300">
                    <img v-if="configuration?.image" :src="configuration.image" :alt="configuration.name || 'Logo'"
                        class="tw:w-full tw:h-full tw:object-cover p-4" />
                    <i v-else class="fas fa-cubes tw:text-brand-primary tw:text-sm"></i>
                </div>
                <span v-show="expanded" class="tw:font-bold tw:text-sm tw:text-white tw:truncate tw:tracking-tight">
                    {{ configuration?.name || 'Panel Admin' }}
                </span>
            </router-link>
        </div>

        <!-- Scrollable Navigation -->
        <div class="tw:flex-1 tw:overflow-hidden">
            <div class="scroll-area tw:py-3">
                <nav id="sidebar-menu" class="tw:p-3 tw:space-y-4">

                    <!-- Home -->
                    <div v-if="ACCESS_MENU(HOME)">
                        <router-link to="/admin/dashboard"
                            class="tw:flex tw:items-center tw:gap-3 tw:px-3.5 tw:py-2.5 tw:rounded-xl tw:text-sm tw:font-medium tw:text-slate-400 tw:no-underline tw:transition-all tw:hover:bg-slate-800/70 tw:hover:text-slate-200"
                            :class="{ 'tw:bg-brand-primary/15 tw:text-brand-primary tw:font-semibold': currentPath == ROUTE_HOME }">
                            <i :class="ACCESS_MENU(HOME).menu.icon" class="tw:text-sm tw:w-4 tw:text-center"></i>
                            <span class="tw:truncate" v-show="expanded">{{ ACCESS_MENU(HOME).menu.title }}</span>
                        </router-link>
                    </div>

                    <!-- Branch Offices -->
                    <div v-if="ACCESS_MENU(BRANCH_OFFICE)">
                        <router-link to="/admin/sucursales"
                            class="tw:flex tw:items-center tw:gap-3 tw:px-3.5 tw:py-2.5 tw:rounded-xl tw:text-sm tw:font-medium tw:text-slate-400 tw:no-underline tw:transition-all tw:hover:bg-slate-800/70 tw:hover:text-slate-200"
                            :class="{ 'tw:bg-brand-primary/15 tw:text-brand-primary tw:font-semibold': [ROUTE_BRANCH_OFFICES, ROUTE_BRANCH_OFFICESADD, ROUTE_BRANCH_OFFICESEDIT, ROUTE_BRANCH_WORKINGHOURS].includes(currentPath) }">
                            <i :class="ACCESS_MENU(BRANCH_OFFICE).menu.icon"
                                class="tw:text-sm tw:w-4 tw:text-center"></i>
                            <span class="tw:truncate" v-show="expanded">{{ ACCESS_MENU(BRANCH_OFFICE).menu.title
                                }}</span>
                        </router-link>
                    </div>

                    <!-- Users Submenu -->
                    <div v-if="ACCESS_MENU(USERS)" class="tw:relative tw:space-y-1">
                        <button type="button" @click="toggleMenu(1)"
                            class="tw:w-full tw:flex tw:items-center tw:justify-between tw:px-3.5 tw:py-2.5 tw:rounded-xl tw:text-sm tw:font-medium tw:text-slate-400 tw:bg-transparent tw:border-0 tw:cursor-pointer tw:transition-all tw:hover:bg-slate-800/70 tw:hover:text-slate-200"
                            :class="{ 'tw:bg-brand-primary/15 tw:text-brand-primary tw:font-semibold': [ROUTE_USER_CLIENTS, ROUTE_USER_ADMIN, ROUTE_USER_FREELANCERS, ROUTE_USER_AGENCIES, ROUTE_USER_ADD, ROUTE_USER_EDIT, ROUTE_USER_PERMISSIONS, ROUTE_USER_BRANCHOFFICES].includes(currentPath) }">
                            <div class="tw:flex tw:items-center tw:gap-3">
                                <i :class="ACCESS_MENU(USERS).menu.icon" class="tw:text-sm tw:w-4 tw:text-center"></i>
                                <span class="tw:truncate" v-show="expanded">{{ ACCESS_MENU(USERS).menu.title }}</span>
                            </div>
                            <i v-if="expanded"
                                class="fas fa-chevron-right tw:text-[10px] tw:text-slate-500 tw:transition-transform tw:duration-200"
                                :class="{ 'tw:rotate-90': openMenu && menuActive == 1 }"></i>
                        </button>

                        <div v-show="openMenu && menuActive == 1" class="tw:pl-9 tw:pr-2 tw:py-1 tw:space-y-1">
                            <router-link v-if="ACCESS_MENU_ACTION(CLIENT_USERS_INDEX)" to="/admin/usuarios/clientes"
                                class="tw:block tw:py-1.5 tw:px-3 tw:rounded-lg tw:text-sm tw:text-slate-400 tw:no-underline tw:transition-colors tw:hover:text-slate-200 tw:hover:bg-slate-800/40"
                                :class="{ 'tw:text-brand-primary tw:font-semibold tw:bg-brand-primary/10': [ROUTE_USER_CLIENTS].includes(currentPath) }">
                                <i class="fa fa-hashtag"></i> Clientes
                            </router-link>
                            <router-link v-if="ACCESS_MENU_ACTION(ADMIN_USERS_INDEX)"
                                to="/admin/usuarios/administradores"
                                class="tw:block tw:py-1.5 tw:px-3 tw:rounded-lg tw:text-sm tw:text-slate-400 tw:no-underline tw:transition-colors tw:hover:text-slate-200 tw:hover:bg-slate-800/40"
                                :class="{ 'tw:text-brand-primary tw:font-semibold tw:bg-brand-primary/10': [ROUTE_USER_ADMIN, ROUTE_USER_PERMISSIONS, ROUTE_USER_BRANCHOFFICES].includes(currentPath) }">
                                <i class="fa fa-hashtag"></i> Administradores
                            </router-link>
                        </div>
                    </div>

                    <!-- Operators -->
                    <div v-if="ACCESS_MENU(OPERATORS)">
                        <router-link to="/admin/operadoras"
                            class="tw:flex tw:items-center tw:gap-3 tw:px-3.5 tw:py-2.5 tw:rounded-xl tw:text-sm tw:font-medium tw:text-slate-400 tw:no-underline tw:transition-all tw:hover:bg-slate-800/70 tw:hover:text-slate-200"
                            :class="{ 'tw:bg-brand-primary/15 tw:text-brand-primary tw:font-semibold': [ROUTE_OPERATORS, ROUTE_OPERATOR_EDIT].includes(currentPath) }">
                            <i :class="ACCESS_MENU(OPERATORS).menu.icon" class="tw:text-sm tw:w-4 tw:text-center"></i>
                            <span class="tw:truncate" v-show="expanded">{{ ACCESS_MENU(OPERATORS).menu.title }}</span>
                        </router-link>
                    </div>

                    <!-- Conveyors -->
                    <div v-if="ACCESS_MENU(CONVEYORS)">
                        <router-link to="/admin/transportadoras"
                            class="tw:flex tw:items-center tw:gap-3 tw:px-3.5 tw:py-2.5 tw:rounded-xl tw:text-sm tw:font-medium tw:text-slate-400 tw:no-underline tw:transition-all tw:hover:bg-slate-800/70 tw:hover:text-slate-200"
                            :class="{ 'tw:bg-brand-primary/15 tw:text-brand-primary tw:font-semibold': [ROUTE_TRANSPORTS, ROUTE_TRANSPORT_EDIT].includes(currentPath) }">
                            <i :class="ACCESS_MENU(CONVEYORS).menu.icon" class="tw:text-sm tw:w-4 tw:text-center"></i>
                            <span class="tw:truncate" v-show="expanded">{{ ACCESS_MENU(CONVEYORS).menu.title }}</span>
                        </router-link>
                    </div>

                    <!-- Units -->
                    <div v-if="ACCESS_MENU(UNITS)">
                        <router-link to="/admin/unidades"
                            class="tw:flex tw:items-center tw:gap-3 tw:px-3.5 tw:py-2.5 tw:rounded-xl tw:text-sm tw:font-medium tw:text-slate-400 tw:no-underline tw:transition-all tw:hover:bg-slate-800/70 tw:hover:text-slate-200"
                            :class="{ 'tw:bg-brand-primary/15 tw:text-brand-primary tw:font-semibold': [ROUTE_UNITS].includes(currentPath) }">
                            <i :class="ACCESS_MENU(UNITS).menu.icon" class="tw:text-sm tw:w-4 tw:text-center"></i>
                            <span class="tw:truncate" v-show="expanded">{{ ACCESS_MENU(UNITS).menu.title }}</span>
                        </router-link>
                    </div>

                    <!-- Services Submenu -->
                    <div v-if="ACCESS_MENU(SERVICES)" class="tw:relative tw:space-y-1">
                        <button type="button" @click="toggleMenu(2)"
                            class="tw:w-full tw:flex tw:items-center tw:justify-between tw:px-3.5 tw:py-2.5 tw:rounded-xl tw:text-sm tw:font-medium tw:text-slate-400 tw:bg-transparent tw:border-0 tw:cursor-pointer tw:transition-all tw:hover:bg-slate-800/70 tw:hover:text-slate-200"
                            :class="{ 'tw:bg-brand-primary/15 tw:text-brand-primary tw:font-semibold': [ROUTE_SERVICE_HOTELS, ROUTE_SERVICE_CIRCUITS, ROUTE_SERVICE_DAYTRIPS, ROUTE_SERVICE_TOURS, ROUTE_SERVICE_INTERNATIONALS, ROUTE_SERVICE_EDIT, ROUTE_SERVICE_TRAVEL_DATE, ROUTE_SERVICE_EXIT_PLACES, ROUTE_SERVICE_EXIT_PLACE_PRICES, ROUTE_SERVICE_LOCKEDS, ROUTE_SERVICE_LOCKED_SHOW, ROUTE_SERVICE_GROUPS, ROUTE_SERVICE_GROUP_SHOW].includes(currentPath) }">
                            <div class="tw:flex tw:items-center tw:gap-3">
                                <i :class="ACCESS_MENU(SERVICES).menu.icon"
                                    class="tw:text-sm tw:w-4 tw:text-center"></i>
                                <span class="tw:truncate" v-show="expanded">{{ ACCESS_MENU(SERVICES).menu.title
                                    }}</span>
                            </div>
                            <i v-if="expanded"
                                class="fas fa-chevron-right tw:text-[10px] tw:text-slate-500 tw:transition-transform tw:duration-200"
                                :class="{ 'tw:rotate-90': openMenu && menuActive == 2 }"></i>
                        </button>

                        <div v-show="openMenu && menuActive == 2" class="tw:pl-9 tw:pr-2 tw:py-1 tw:space-y-1">
                            <router-link v-if="ACCESS_MENU_ACTION(ROUTE_SERVICE_HOTELS)" to="/admin/servicios/hoteles"
                                class="tw:block tw:py-1.5 tw:px-3 tw:rounded-lg tw:text-sm tw:text-slate-400 tw:no-underline tw:transition-colors tw:hover:text-slate-200 tw:hover:bg-slate-800/40"
                                :class="{ 'tw:text-brand-primary tw:font-semibold tw:bg-brand-primary/10': [ROUTE_SERVICE_HOTELS].includes(currentPath) }"><i
                                    class="fa fa-hashtag tw:mr-2"></i> Hoteles</router-link>
                            <router-link v-if="ACCESS_MENU_ACTION(ROUTE_SERVICE_CIRCUITS)"
                                to="/admin/servicios/circuitos"
                                class="tw:block tw:py-1.5 tw:px-3 tw:rounded-lg tw:text-sm tw:text-slate-400 tw:no-underline tw:transition-colors tw:hover:text-slate-200 tw:hover:bg-slate-800/40"
                                :class="{ 'tw:text-brand-primary tw:font-semibold tw:bg-brand-primary/10': [ROUTE_SERVICE_CIRCUITS].includes(currentPath) }">
                                <i class="fa fa-hashtag tw:mr-2"></i> Circuitos</router-link>
                            <router-link v-if="ACCESS_MENU_ACTION(ROUTE_SERVICE_DAYTRIPS)"
                                to="/admin/servicios/pasadias"
                                class="tw:block tw:py-1.5 tw:px-3 tw:rounded-lg tw:text-sm tw:text-slate-400 tw:no-underline tw:transition-colors tw:hover:text-slate-200 tw:hover:bg-slate-800/40"
                                :class="{ 'tw:text-brand-primary tw:font-semibold tw:bg-brand-primary/10': [ROUTE_SERVICE_DAYTRIPS].includes(currentPath) }"><i
                                    class="fa fa-hashtag tw:mr-2"></i> Pasadias</router-link>
                            <router-link v-if="ACCESS_MENU_ACTION(ROUTE_SERVICE_TOURS)" to="/admin/servicios/tours"
                                class="tw:block tw:py-1.5 tw:px-3 tw:rounded-lg tw:text-sm tw:text-slate-400 tw:no-underline tw:transition-colors tw:hover:text-slate-200 tw:hover:bg-slate-800/40"
                                :class="{ 'tw:text-brand-primary tw:font-semibold tw:bg-brand-primary/10': [ROUTE_SERVICE_TOURS].includes(currentPath) }"><i
                                    class="fa fa-hashtag tw:mr-2"></i> Tours</router-link>
                            <router-link v-if="ACCESS_MENU_ACTION(ROUTE_SERVICE_INTERNATIONALS)"
                                to="/admin/servicios/internacionales"
                                class="tw:block tw:py-1.5 tw:px-3 tw:rounded-lg tw:text-sm tw:text-slate-400 tw:no-underline tw:transition-colors tw:hover:text-slate-200 tw:hover:bg-slate-800/40"
                                :class="{ 'tw:text-brand-primary tw:font-semibold tw:bg-brand-primary/10': [ROUTE_SERVICE_INTERNATIONALS].includes(currentPath) }"><i
                                    class="fa fa-hashtag tw:mr-2"></i> Internacionales</router-link>
                            <router-link v-if="ACCESS_MENU_ACTION(ROUTE_SERVICE_LOCKEDS)" to="/admin/servicios/bloqueos"
                                class="tw:block tw:py-1.5 tw:px-3 tw:rounded-lg tw:text-sm tw:text-slate-400 tw:no-underline tw:transition-colors tw:hover:text-slate-200 tw:hover:bg-slate-800/40"
                                :class="{ 'tw:text-brand-primary tw:font-semibold tw:bg-brand-primary/10': [ROUTE_SERVICE_LOCKEDS, ROUTE_SERVICE_LOCKED_SHOW].includes(currentPath) }"><i
                                    class="fa fa-hashtag tw:mr-2"></i> Bloqueos</router-link>
                            <router-link v-if="ACCESS_MENU_ACTION(ROUTE_SERVICE_GROUPS)" to="/admin/servicios/grupos"
                                class="tw:block tw:py-1.5 tw:px-3 tw:rounded-lg tw:text-sm tw:text-slate-400 tw:no-underline tw:transition-colors tw:hover:text-slate-200 tw:hover:bg-slate-800/40"
                                :class="{ 'tw:text-brand-primary tw:font-semibold tw:bg-brand-primary/10': [ROUTE_SERVICE_GROUPS, ROUTE_SERVICE_GROUP_SHOW].includes(currentPath) }"><i
                                    class="fa fa-hashtag tw:mr-2"></i> Grupos</router-link>
                        </div>
                    </div>

                    <!-- Cotizador -->
                    <div v-if="ACCESS_MENU(SERVICE_QUOTE)">
                        <router-link to="/admin/cotizador"
                            class="tw:flex tw:items-center tw:gap-3 tw:px-3.5 tw:py-2.5 tw:rounded-xl tw:text-sm tw:font-medium tw:text-slate-400 tw:no-underline tw:transition-all tw:hover:bg-slate-800/70 tw:hover:text-slate-200"
                            :class="{ 'tw:bg-brand-primary/15 tw:text-brand-primary tw:font-semibold': [ROUTE_SERVICE_QUOTE, ROUTE_SERVICE_QUOTE_ADD].includes(currentPath) }">
                            <i :class="ACCESS_MENU(SERVICE_QUOTE).menu.icon"
                                class="tw:text-sm tw:w-4 tw:text-center"></i>
                            <span class="tw:truncate" v-show="expanded">{{ ACCESS_MENU(SERVICE_QUOTE).menu.title
                                }}</span>
                        </router-link>
                    </div>

                    <!-- Reservations Submenu -->
                    <div v-if="ACCESS_MENU(RESERVATIONS)" class="tw:relative tw:space-y-1">
                        <button type="button" @click="toggleMenu(3)"
                            class="tw:w-full tw:flex tw:items-center tw:justify-between tw:px-3.5 tw:py-2.5 tw:rounded-xl tw:text-sm tw:font-medium tw:text-slate-400 tw:bg-transparent tw:border-0 tw:cursor-pointer tw:transition-all tw:hover:bg-slate-800/70 tw:hover:text-slate-200"
                            :class="{ 'tw:bg-brand-primary/15 tw:text-brand-primary tw:font-semibold': [ROUTE_RESERVATION_HOTEL_INDEX, ROUTE_RESERVATION_HOTEL_CREATE, ROUTE_RESERVATION_HOTEL_SHOW, ROUTE_RESERVATION_HOTEL_EDIT, ROUTE_RESERVATION_CIRCUIT_INDEX, ROUTE_RESERVATION_CIRCUIT_CREATE, ROUTE_RESERVATION_CIRCUIT_SHOW, ROUTE_RESERVATION_CIRCUIT_EDIT, ROUTE_RESERVATION_DAYTRIPS_INDEX, ROUTE_RESERVATION_DAYTRIPS_CREATE, ROUTE_RESERVATION_DAYTRIPS_SHOW, ROUTE_RESERVATION_DAYTRIPS_EDIT, ROUTE_RESERVATION_INTERNATIONALS_INDEX, ROUTE_RESERVATION_INTERNATIONALS_CREATE, ROUTE_RESERVATION_INTERNATIONALS_SHOW, ROUTE_RESERVATION_INTERNATIONALS_EDIT, ROUTE_RESERVATION_FLIGHTS_INDEX, ROUTE_RESERVATION_FLIGHTS_CREATE, ROUTE_RESERVATION_FLIGHTS_SHOW, ROUTE_RESERVATION_FLIGHTS_EDIT, ROUTE_RESERVATION_OTHER_SERVICE_INDEX, ROUTE_RESERVATION_OTHER_SERVICE_CREATE, ROUTE_RESERVATION_OTHER_SERVICE_SHOW, ROUTE_RESERVATION_OTHER_SERVICE_EDIT, ROUTE_RESERVATION_SELECT_SEAT_INDEX, ROUTE_RESERVATION_NOTES, ROUTE_RESERVATION_PEOPLE, ROUTE_RESERVATION_PAYMENTS, ROUTE_RESERVATION_LIST_PAYMENTS, ROUTE_RESERVATION_PAYMENT_TO_BROOKER, ROUTE_RESERVATION_TRANSFER_INDEX, ROUTE_RESERVATION_TRANSFER_CREATE, ROUTE_RESERVATION_TRANSFER_SHOW, ROUTE_RESERVATION_TRANSFER_EDIT, ROUTE_RESERVATION_LOCKEDS_INDEX, ROUTE_RESERVATION_LOCKEDS_CREATE, ROUTE_RESERVATION_LOCKEDS_SHOW, ROUTE_RESERVATION_LOCKEDS_EDIT, ROUTE_RESERVATION_GROUPS_INDEX, ROUTE_RESERVATION_GROUPS_CREATE, ROUTE_RESERVATION_GROUPS_SHOW, ROUTE_RESERVATION_GROUPS_EDIT].includes(currentPath) }">
                            <div class="tw:flex tw:items-center tw:gap-3">
                                <i :class="ACCESS_MENU(RESERVATIONS).menu.icon"
                                    class="tw:text-sm tw:w-4 tw:text-center"></i>
                                <span class="tw:truncate" v-show="expanded">{{ ACCESS_MENU(RESERVATIONS).menu.title
                                    }}</span>
                            </div>
                            <i v-if="expanded"
                                class="fas fa-chevron-right tw:text-[10px] tw:text-slate-500 tw:transition-transform tw:duration-200"
                                :class="{ 'tw:rotate-90': openMenu && menuActive == 3 }"></i>
                        </button>

                        <div v-show="openMenu && menuActive == 3" class="tw:pl-9 tw:pr-2 tw:py-1 tw:space-y-1">
                            <router-link v-if="ACCESS_MENU_ACTION(HOTEL_RESERVATIONS_INDEX)"
                                to="/admin/reservaciones/hoteles"
                                class="tw:block tw:py-1.5 tw:px-3 tw:rounded-lg tw:text-sm tw:text-slate-400 tw:no-underline tw:transition-colors tw:hover:text-slate-200 tw:hover:bg-slate-800/40"
                                :class="{ 'tw:text-brand-primary tw:font-semibold tw:bg-brand-primary/10': [ROUTE_RESERVATION_HOTEL_INDEX, ROUTE_RESERVATION_HOTEL_CREATE, ROUTE_RESERVATION_HOTEL_SHOW, ROUTE_RESERVATION_HOTEL_EDIT].includes(currentPath) }"><i
                                    class="fa fa-hashtag tw:mr-2"></i> Hoteles</router-link>
                            <router-link v-if="ACCESS_MENU_ACTION(CIRCUIT_RESERVATIONS_INDEX)"
                                to="/admin/reservaciones/circuitos"
                                class="tw:block tw:py-1.5 tw:px-3 tw:rounded-lg tw:text-sm tw:text-slate-400 tw:no-underline tw:transition-colors tw:hover:text-slate-200 tw:hover:bg-slate-800/40"
                                :class="{ 'tw:text-brand-primary tw:font-semibold tw:bg-brand-primary/10': [ROUTE_RESERVATION_CIRCUIT_INDEX, ROUTE_RESERVATION_CIRCUIT_CREATE, ROUTE_RESERVATION_CIRCUIT_SHOW, ROUTE_RESERVATION_CIRCUIT_EDIT].includes(currentPath) }"><i
                                    class="fa fa-hashtag tw:mr-2"></i> Circuitos</router-link>
                            <router-link v-if="ACCESS_MENU_ACTION(DAYTRIPS_RESERVATIONS_INDEX)"
                                to="/admin/reservaciones/pasadias"
                                class="tw:block tw:py-1.5 tw:px-3 tw:rounded-lg tw:text-sm tw:text-slate-400 tw:no-underline tw:transition-colors tw:hover:text-slate-200 tw:hover:bg-slate-800/40"
                                :class="{ 'tw:text-brand-primary tw:font-semibold tw:bg-brand-primary/10': [ROUTE_RESERVATION_DAYTRIPS_INDEX, ROUTE_RESERVATION_DAYTRIPS_CREATE, ROUTE_RESERVATION_DAYTRIPS_SHOW, ROUTE_RESERVATION_DAYTRIPS_EDIT].includes(currentPath) }"><i
                                    class="fa fa-hashtag tw:mr-2"></i> Pasadias</router-link>
                            <router-link v-if="ACCESS_MENU_ACTION(TOURS_RESERVATIONS_INDEX)"
                                to="/admin/reservaciones/tours"
                                class="tw:block tw:py-1.5 tw:px-3 tw:rounded-lg tw:text-sm tw:text-slate-400 tw:no-underline tw:transition-colors tw:hover:text-slate-200 tw:hover:bg-slate-800/40"
                                :class="{ 'tw:text-brand-primary tw:font-semibold tw:bg-brand-primary/10': [ROUTE_RESERVATION_TOURS_INDEX, ROUTE_RESERVATION_TOURS_CREATE, ROUTE_RESERVATION_TOURS_SHOW, ROUTE_RESERVATION_TOURS_EDIT].includes(currentPath) }"><i
                                    class="fa fa-hashtag tw:mr-2"></i> Tours</router-link>
                            <router-link v-if="ACCESS_MENU_ACTION(INTERNATIONAL_RESERVATIONS_INDEX)"
                                to="/admin/reservaciones/internacionales"
                                class="tw:block tw:py-1.5 tw:px-3 tw:rounded-lg tw:text-sm tw:text-slate-400 tw:no-underline tw:transition-colors tw:hover:text-slate-200 tw:hover:bg-slate-800/40"
                                :class="{ 'tw:text-brand-primary tw:font-semibold tw:bg-brand-primary/10': [ROUTE_RESERVATION_INTERNATIONALS_INDEX, ROUTE_RESERVATION_INTERNATIONALS_CREATE, ROUTE_RESERVATION_INTERNATIONALS_SHOW, ROUTE_RESERVATION_INTERNATIONALS_EDIT].includes(currentPath) }"><i
                                    class="fa fa-hashtag tw:mr-2"></i> Internacionales</router-link>
                            <router-link v-if="ACCESS_MENU_ACTION(PACKAGED_RESERVATIONS_INDEX)"
                                to="/admin/reservaciones/empaquetados"
                                class="tw:block tw:py-1.5 tw:px-3 tw:rounded-lg tw:text-sm tw:text-slate-400 tw:no-underline tw:transition-colors tw:hover:text-slate-200 tw:hover:bg-slate-800/40"
                                :class="{ 'tw:text-brand-primary tw:font-semibold tw:bg-brand-primary/10': [ROUTE_RESERVATION_PACKAGED_INDEX, ROUTE_RESERVATION_PACKAGED_CREATE, ROUTE_RESERVATION_PACKAGED_SHOW, ROUTE_RESERVATION_PACKAGED_EDIT].includes(currentPath) }"><i
                                    class="fa fa-hashtag tw:mr-2"></i> Empaquetados</router-link>
                            <router-link v-if="ACCESS_MENU_ACTION(FLIGHT_RESERVATIONS_INDEX)"
                                to="/admin/reservaciones/vuelos"
                                class="tw:block tw:py-1.5 tw:px-3 tw:rounded-lg tw:text-sm tw:text-slate-400 tw:no-underline tw:transition-colors tw:hover:text-slate-200 tw:hover:bg-slate-800/40"
                                :class="{ 'tw:text-brand-primary tw:font-semibold tw:bg-brand-primary/10': [ROUTE_RESERVATION_FLIGHTS_INDEX, ROUTE_RESERVATION_FLIGHTS_CREATE, ROUTE_RESERVATION_FLIGHTS_SHOW, ROUTE_RESERVATION_FLIGHTS_EDIT].includes(currentPath) }"><i
                                    class="fa fa-hashtag tw:mr-2"></i> Vuelos</router-link>
                            <router-link v-if="ACCESS_MENU_ACTION(OTHER_SERVICES_RESERVATIONS_INDEX)"
                                to="/admin/reservaciones/otros-servicios"
                                class="tw:block tw:py-1.5 tw:px-3 tw:rounded-lg tw:text-sm tw:text-slate-400 tw:no-underline tw:transition-colors tw:hover:text-slate-200 tw:hover:bg-slate-800/40"
                                :class="{ 'tw:text-brand-primary tw:font-semibold tw:bg-brand-primary/10': [ROUTE_RESERVATION_OTHER_SERVICE_INDEX, ROUTE_RESERVATION_OTHER_SERVICE_CREATE, ROUTE_RESERVATION_OTHER_SERVICE_SHOW, ROUTE_RESERVATION_OTHER_SERVICE_EDIT].includes(currentPath) }"><i
                                    class="fa fa-hashtag tw:mr-2"></i> Otros
                                Servicios</router-link>
                            <router-link v-if="ACCESS_MENU_ACTION(OTHER_SERVICES_RESERVATIONS_INDEX)"
                                to="/admin/reservaciones/traslados"
                                class="tw:block tw:py-1.5 tw:px-3 tw:rounded-lg tw:text-sm tw:text-slate-400 tw:no-underline tw:transition-colors tw:hover:text-slate-200 tw:hover:bg-slate-800/40"
                                :class="{ 'tw:text-brand-primary tw:font-semibold tw:bg-brand-primary/10': [ROUTE_RESERVATION_TRANSFER_INDEX, ROUTE_RESERVATION_TRANSFER_CREATE, ROUTE_RESERVATION_TRANSFER_SHOW, ROUTE_RESERVATION_TRANSFER_EDIT].includes(currentPath) }"><i
                                    class="fa fa-hashtag tw:mr-2"></i> Traslados</router-link>
                            <router-link v-if="ACCESS_MENU_ACTION(LOCKED_RESERVATIONS_INDEX)"
                                to="/admin/reservaciones/bloqueos"
                                class="tw:block tw:py-1.5 tw:px-3 tw:rounded-lg tw:text-sm tw:text-slate-400 tw:no-underline tw:transition-colors tw:hover:text-slate-200 tw:hover:bg-slate-800/40"
                                :class="{ 'tw:text-brand-primary tw:font-semibold tw:bg-brand-primary/10': [ROUTE_RESERVATION_LOCKEDS_INDEX, ROUTE_RESERVATION_LOCKEDS_CREATE, ROUTE_RESERVATION_LOCKEDS_EDIT, ROUTE_RESERVATION_LOCKEDS_SHOW].includes(currentPath) }"><i
                                    class="fa fa-hashtag tw:mr-2"></i> Bloqueos</router-link>
                            <router-link v-if="ACCESS_MENU_ACTION(GROUP_RESERVATIONS_INDEX)"
                                to="/admin/reservaciones/grupos"
                                class="tw:block tw:py-1.5 tw:px-3 tw:rounded-lg tw:text-sm tw:text-slate-400 tw:no-underline tw:transition-colors tw:hover:text-slate-200 tw:hover:bg-slate-800/40"
                                :class="{ 'tw:text-brand-primary tw:font-semibold tw:bg-brand-primary/10': [ROUTE_RESERVATION_GROUPS_INDEX, ROUTE_RESERVATION_GROUPS_CREATE, ROUTE_RESERVATION_GROUPS_EDIT, ROUTE_RESERVATION_GROUPS_SHOW].includes(currentPath) }"><i
                                    class="fa fa-hashtag tw:mr-2"></i> Grupos</router-link>
                        </div>
                    </div>

                    <!-- Multiple Payments -->
                    <div v-if="ACCESS_MENU(PAYMENT_MULTIPLE)">
                        <router-link to="/admin/pagos-multiples"
                            class="tw:flex tw:items-center tw:gap-3 tw:px-3.5 tw:py-2.5 tw:rounded-xl tw:text-sm tw:font-medium tw:text-slate-400 tw:no-underline tw:transition-all tw:hover:bg-slate-800/70 tw:hover:text-slate-200"
                            :class="{ 'tw:bg-brand-primary/15 tw:text-brand-primary tw:font-semibold': [ROUTE_PAYMENT_MULTIPLE, ROUTE_PAYMENT_MULTIPLE_ADD].includes(currentPath) }">
                            <i :class="ACCESS_MENU(PAYMENT_MULTIPLE).menu.icon"
                                class="tw:text-sm tw:w-4 tw:text-center"></i>
                            <span class="tw:truncate" v-show="expanded">{{ ACCESS_MENU(PAYMENT_MULTIPLE).menu.title
                                }}</span>
                        </router-link>
                    </div>

                    <!-- Coupons -->
                    <div v-if="ACCESS_MENU(COUPON)">
                        <router-link to="/admin/cupones"
                            class="tw:flex tw:items-center tw:gap-3 tw:px-3.5 tw:py-2.5 tw:rounded-xl tw:text-sm tw:font-medium tw:text-slate-400 tw:no-underline tw:transition-all tw:hover:bg-slate-800/70 tw:hover:text-slate-200"
                            :class="{ 'tw:bg-brand-primary/15 tw:text-brand-primary tw:font-semibold': [ROUTE_COUPONS, ROUTE_COUPON_ADD].includes(currentPath) }">
                            <i :class="ACCESS_MENU(COUPON).menu.icon" class="tw:text-sm tw:w-4 tw:text-center"></i>
                            <span class="tw:truncate" v-show="expanded">{{ ACCESS_MENU(COUPON).menu.title }}</span>
                        </router-link>
                    </div>

                    <!-- Boxes -->
                    <div v-if="ACCESS_MENU(BOX)">
                        <router-link to="/admin/cajas"
                            class="tw:flex tw:items-center tw:gap-3 tw:px-3.5 tw:py-2.5 tw:rounded-xl tw:text-sm tw:font-medium tw:text-slate-400 tw:no-underline tw:transition-all tw:hover:bg-slate-800/70 tw:hover:text-slate-200"
                            :class="{ 'tw:bg-brand-primary/15 tw:text-brand-primary tw:font-semibold': [ROUTE_BRANCH_BOXES, ROUTE_BOX_HISTORY, ROUTE_BOX_HISTORY_MOVE_LIST].includes(currentPath) }">
                            <i :class="ACCESS_MENU(BOX).menu.icon" class="tw:text-sm tw:w-4 tw:text-center"></i>
                            <span class="tw:truncate" v-show="expanded">{{ ACCESS_MENU(BOX).menu.title }}</span>
                        </router-link>
                    </div>

                    <!-- Savings -->
                    <div v-if="ACCESS_MENU(SAVING)">
                        <router-link to="/admin/ahorros"
                            class="tw:flex tw:items-center tw:gap-3 tw:px-3.5 tw:py-2.5 tw:rounded-xl tw:text-sm tw:font-medium tw:text-slate-400 tw:no-underline tw:transition-all tw:hover:bg-slate-800/70 tw:hover:text-slate-200"
                            :class="{ 'tw:bg-brand-primary/15 tw:text-brand-primary tw:font-semibold': [ROUTE_SAVING].includes(currentPath) }">
                            <i :class="ACCESS_MENU(SAVING).menu.icon" class="tw:text-sm tw:w-4 tw:text-center"></i>
                            <span class="tw:truncate" v-show="expanded">{{ ACCESS_MENU(SAVING).menu.title }}</span>
                        </router-link>
                    </div>

                    <!-- Promotions -->
                    <div v-if="ACCESS_MENU(PROMOTIONS)">
                        <router-link to="/admin/promociones"
                            class="tw:flex tw:items-center tw:gap-3 tw:px-3.5 tw:py-2.5 tw:rounded-xl tw:text-sm tw:font-medium tw:text-slate-400 tw:no-underline tw:transition-all tw:hover:bg-slate-800/70 tw:hover:text-slate-200"
                            :class="{ 'tw:bg-brand-primary/15 tw:text-brand-primary tw:font-semibold': [ROUTE_PROMOTION_FOLDER, ROUTE_PROMOTIONS, ROUTE_PROMOTION_ADD, ROUTE_PROMOTION_EDIT].includes(currentPath) }">
                            <i :class="ACCESS_MENU(PROMOTIONS).menu.icon" class="tw:text-sm tw:w-4 tw:text-center"></i>
                            <span class="tw:truncate" v-show="expanded">{{ ACCESS_MENU(PROMOTIONS).menu.title }}</span>
                        </router-link>
                    </div>

                    <!-- Banks -->
                    <div v-if="ACCESS_MENU(BANKS)">
                        <router-link to="/admin/cuentas"
                            class="tw:flex tw:items-center tw:gap-3 tw:px-3.5 tw:py-2.5 tw:rounded-xl tw:text-sm tw:font-medium tw:text-slate-400 tw:no-underline tw:transition-all tw:hover:bg-slate-800/70 tw:hover:text-slate-200"
                            :class="{ 'tw:bg-brand-primary/15 tw:text-brand-primary tw:font-semibold': [ROUTE_BANKS, ROUTE_BANK_ADD, ROUTE_BANK_EDIT, ROUTE_BANK_HISTORY_MOVE_LIST].includes(currentPath) }">
                            <i :class="ACCESS_MENU(BANKS).menu.icon" class="tw:text-sm tw:w-4 tw:text-center"></i>
                            <span class="tw:truncate" v-show="expanded">{{ ACCESS_MENU(BANKS).menu.title }}</span>
                        </router-link>
                    </div>

                    <!-- Profiles -->
                    <div v-if="ACCESS_MENU(PROFILES)">
                        <router-link to="/admin/perfiles"
                            class="tw:flex tw:items-center tw:gap-3 tw:px-3.5 tw:py-2.5 tw:rounded-xl tw:text-sm tw:font-medium tw:text-slate-400 tw:no-underline tw:transition-all tw:hover:bg-slate-800/70 tw:hover:text-slate-200"
                            :class="{ 'tw:bg-brand-primary/15 tw:text-brand-primary tw:font-semibold': [ROUTE_PROFILE_LIST, ROUTE_PROFILE_ADD].includes(currentPath) }">
                            <i :class="ACCESS_MENU(PROFILES).menu.icon" class="tw:text-sm tw:w-4 tw:text-center"></i>
                            <span class="tw:truncate" v-show="expanded">{{ ACCESS_MENU(PROFILES).menu.title }}</span>
                        </router-link>
                    </div>

                    <!-- Activity Log -->
                    <div v-if="ACCESS_MENU(ACTIVITY)">
                        <router-link to="/admin/actividades"
                            class="tw:flex tw:items-center tw:gap-3 tw:px-3.5 tw:py-2.5 tw:rounded-xl tw:text-sm tw:font-medium tw:text-slate-400 tw:no-underline tw:transition-all tw:hover:bg-slate-800/70 tw:hover:text-slate-200"
                            :class="{ 'tw:bg-brand-primary/15 tw:text-brand-primary tw:font-semibold': [ROUTE_ACTIVITY].includes(currentPath) }">
                            <i :class="ACCESS_MENU(ACTIVITY).menu.icon" class="tw:text-sm tw:w-4 tw:text-center"></i>
                            <span class="tw:truncate" v-show="expanded">{{ ACCESS_MENU(ACTIVITY).menu.title }}</span>
                        </router-link>
                    </div>

                    <!-- Reports -->
                    <div v-if="ACCESS_MENU(REPORTS)">
                        <router-link to="/admin/reportes"
                            class="tw:flex tw:items-center tw:gap-3 tw:px-3.5 tw:py-2.5 tw:rounded-xl tw:text-sm tw:font-medium tw:text-slate-400 tw:no-underline tw:transition-all tw:hover:bg-slate-800/70 tw:hover:text-slate-200"
                            :class="{ 'tw:bg-brand-primary/15 tw:text-brand-primary tw:font-semibold': [ROUTE_REPORTS].includes(currentPath) }">
                            <i :class="ACCESS_MENU(REPORTS).menu.icon" class="tw:text-sm tw:w-4 tw:text-center"></i>
                            <span class="tw:truncate" v-show="expanded">{{ ACCESS_MENU(REPORTS).menu.title }}</span>
                        </router-link>
                    </div>

                    <!-- Travel Gallery -->
                    <div v-if="ACCESS_MENU(TRAVEL_GALLERY)">
                        <router-link to="/admin/galeria-viajes"
                            class="tw:flex tw:items-center tw:gap-3 tw:px-3.5 tw:py-2.5 tw:rounded-xl tw:text-sm tw:font-medium tw:text-slate-400 tw:no-underline tw:transition-all tw:hover:bg-slate-800/70 tw:hover:text-slate-200"
                            :class="{ 'tw:bg-brand-primary/15 tw:text-brand-primary tw:font-semibold': [ROUTE_TRAVEL_GALLERY_INDEX].includes(currentPath) }">
                            <i :class="ACCESS_MENU(TRAVEL_GALLERY).menu.icon"
                                class="tw:text-sm tw:w-4 tw:text-center"></i>
                            <span class="tw:truncate" v-show="expanded">{{ ACCESS_MENU(TRAVEL_GALLERY).menu.title
                                }}</span>
                        </router-link>
                    </div>
                </nav>
            </div>
        </div>

    </aside>
</template>

<script>
import { ACCESS_MENU, ACCESS_MENU_ACTION } from '@/validations/auth/AccessControlList';
import {
    ADMIN_USERS_INDEX, CLIENT_USERS_INDEX, HOTEL_RESERVATIONS_INDEX,
    CIRCUIT_RESERVATIONS_INDEX,
    DAYTRIPS_RESERVATIONS_INDEX,
    TOURS_RESERVATIONS_INDEX,
    INTERNATIONAL_RESERVATIONS_INDEX,
    FLIGHT_RESERVATIONS_INDEX,
    OTHER_SERVICES_RESERVATIONS_INDEX,
    PACKAGED_RESERVATIONS_INDEX,
    LOCKED_RESERVATIONS_INDEX,
    GROUP_RESERVATIONS_INDEX,
} from '@/enums/MenuActionEnums';
import {
    HOME, BRANCH_OFFICE, USERS, OPERATORS, CONVEYORS, UNITS, SERVICES, RESERVATIONS, BOX,
    SAVING, PROMOTIONS, BANKS, PROFILES, COUPON, PAYMENT_MULTIPLE, SERVICE_QUOTE, ACTIVITY, REPORTS, TRAVEL_GALLERY
} from "@/enums/MenuEnums";
import {
    ROUTE_HOME,
    ROUTE_BRANCH_OFFICES,
    ROUTE_BRANCH_OFFICESADD,
    ROUTE_BRANCH_OFFICESEDIT,
    ROUTE_BRANCH_WORKINGHOURS,
    ROUTE_USER_ADMIN,
    ROUTE_USER_CLIENTS,
    ROUTE_USER_FREELANCERS,
    ROUTE_USER_AGENCIES,
    ROUTE_USER_ADD,
    ROUTE_USER_EDIT,
    ROUTE_OPERATORS,
    ROUTE_OPERATOR_EDIT,
    ROUTE_TRANSPORTS,
    ROUTE_TRANSPORT_EDIT,
    ROUTE_UNITS,
    ROUTE_SERVICE_HOTELS,
    ROUTE_SERVICE_CIRCUITS,
    ROUTE_SERVICE_DAYTRIPS,
    ROUTE_SERVICE_TOURS,
    ROUTE_SERVICE_INTERNATIONALS,
    ROUTE_SERVICE_LOCKEDS,
    ROUTE_SERVICE_LOCKED_SHOW,
    ROUTE_SERVICE_GROUPS,
    ROUTE_SERVICE_GROUP_SHOW,
    ROUTE_SERVICE_EDIT,
    ROUTE_SERVICE_TRAVEL_DATE,
    ROUTE_SERVICE_EXIT_PLACES,
    ROUTE_SERVICE_EXIT_PLACE_PRICES,
    ROUTE_RESERVATION_HOTEL_INDEX,
    ROUTE_RESERVATION_HOTEL_CREATE,
    ROUTE_RESERVATION_HOTEL_SHOW,
    ROUTE_RESERVATION_HOTEL_EDIT,
    ROUTE_RESERVATION_CIRCUIT_INDEX,
    ROUTE_RESERVATION_CIRCUIT_CREATE,
    ROUTE_RESERVATION_CIRCUIT_SHOW,
    ROUTE_RESERVATION_CIRCUIT_EDIT,
    ROUTE_RESERVATION_DAYTRIPS_INDEX,
    ROUTE_RESERVATION_DAYTRIPS_CREATE,
    ROUTE_RESERVATION_DAYTRIPS_SHOW,
    ROUTE_RESERVATION_DAYTRIPS_EDIT,
    ROUTE_RESERVATION_TOURS_INDEX,
    ROUTE_RESERVATION_TOURS_CREATE,
    ROUTE_RESERVATION_TOURS_SHOW,
    ROUTE_RESERVATION_TOURS_EDIT,
    ROUTE_USER_PERMISSIONS,
    ROUTE_USER_BRANCHOFFICES,
    ROUTE_RESERVATION_INTERNATIONALS_INDEX,
    ROUTE_RESERVATION_INTERNATIONALS_CREATE,
    ROUTE_RESERVATION_INTERNATIONALS_SHOW,
    ROUTE_RESERVATION_INTERNATIONALS_EDIT,
    ROUTE_RESERVATION_FLIGHTS_INDEX,
    ROUTE_RESERVATION_FLIGHTS_CREATE,
    ROUTE_RESERVATION_FLIGHTS_SHOW,
    ROUTE_RESERVATION_FLIGHTS_EDIT,
    ROUTE_RESERVATION_OTHER_SERVICE_INDEX,
    ROUTE_RESERVATION_OTHER_SERVICE_CREATE,
    ROUTE_RESERVATION_OTHER_SERVICE_SHOW,
    ROUTE_RESERVATION_OTHER_SERVICE_EDIT,
    ROUTE_RESERVATION_NOTES,
    ROUTE_RESERVATION_SELECT_SEAT_INDEX,
    ROUTE_PROFILE_LIST,
    ROUTE_PROFILE_ADD,
    ROUTE_PROMOTIONS,
    ROUTE_PROMOTION_ADD,
    ROUTE_PROMOTION_EDIT,
    ROUTE_COUPONS,
    ROUTE_COUPON_ADD,
    ROUTE_BANKS,
    ROUTE_BANK_ADD,
    ROUTE_BANK_EDIT,
    ROUTE_RESERVATION_PAYMENTS,
    ROUTE_RESERVATION_LIST_PAYMENTS,
    ROUTE_RESERVATION_PEOPLE,
    ROUTE_BRANCH_BOXES,
    ROUTE_BOX_HISTORY,
    ROUTE_BOX_HISTORY_MOVE_LIST,
    ROUTE_SAVING,
    ROUTE_BANK_HISTORY_MOVE_LIST,
    ROUTE_RESERVATION_PAYMENT_TO_BROOKER,
    ROUTE_PROMOTION_FOLDER,
    ROUTE_RESERVATION_PACKAGED_INDEX,
    ROUTE_RESERVATION_PACKAGED_CREATE,
    ROUTE_RESERVATION_PACKAGED_SHOW,
    ROUTE_RESERVATION_PACKAGED_EDIT,
    ROUTE_ACTIVITY,
    ROUTE_REPORTS,
    ROUTE_RESERVATION_TRANSFER_INDEX,
    ROUTE_RESERVATION_TRANSFER_CREATE,
    ROUTE_RESERVATION_TRANSFER_SHOW,
    ROUTE_RESERVATION_TRANSFER_EDIT,
    ROUTE_PAYMENT_MULTIPLE,
    ROUTE_PAYMENT_MULTIPLE_ADD,
    ROUTE_SERVICE_QUOTE,
    ROUTE_SERVICE_QUOTE_ADD,
    ROUTE_RESERVATION_LOCKEDS_INDEX,
    ROUTE_RESERVATION_LOCKEDS_CREATE,
    ROUTE_RESERVATION_LOCKEDS_SHOW,
    ROUTE_RESERVATION_LOCKEDS_EDIT,
    ROUTE_RESERVATION_GROUPS_INDEX,
    ROUTE_RESERVATION_GROUPS_CREATE,
    ROUTE_RESERVATION_GROUPS_SHOW,
    ROUTE_RESERVATION_GROUPS_EDIT,
    ROUTE_TRAVEL_GALLERY_INDEX,
} from "@/enums/RouteNameEnums";
import { mapGetters } from "vuex";
import { isSidebarCollapsed, isSidebarEffectivelyCollapsed, setSidebarHovering } from '@/plugins/sidebarStore';

export default {
    data() {
        return {
            activeClass: 'active',
            openMenu: true,
            menuActive: 0,
            subdomain: this.$subdomain ?? "",

            // ACTIONS
            ADMIN_USERS_INDEX, CLIENT_USERS_INDEX, HOTEL_RESERVATIONS_INDEX,
            CIRCUIT_RESERVATIONS_INDEX,
            DAYTRIPS_RESERVATIONS_INDEX,
            TOURS_RESERVATIONS_INDEX,
            INTERNATIONAL_RESERVATIONS_INDEX,
            FLIGHT_RESERVATIONS_INDEX,
            OTHER_SERVICES_RESERVATIONS_INDEX,
            PACKAGED_RESERVATIONS_INDEX,
            LOCKED_RESERVATIONS_INDEX,
            GROUP_RESERVATIONS_INDEX,

            // MENU IDS
            HOME, BRANCH_OFFICE, USERS, OPERATORS,
            CONVEYORS, UNITS, SERVICES,
            RESERVATIONS, BOX,
            SAVING, PROMOTIONS, BANKS, PROFILES, COUPON, PAYMENT_MULTIPLE, ACTIVITY, REPORTS, TRAVEL_GALLERY,
            SERVICE_QUOTE,

            // ROUTE NAMES COMPONENTS
            ROUTE_HOME,
            ROUTE_BRANCH_OFFICES,
            ROUTE_BRANCH_OFFICESADD,
            ROUTE_BRANCH_OFFICESEDIT,
            ROUTE_BRANCH_WORKINGHOURS,
            ROUTE_USER_ADMIN,
            ROUTE_USER_CLIENTS,
            ROUTE_USER_FREELANCERS,
            ROUTE_USER_AGENCIES,
            ROUTE_USER_ADD,
            ROUTE_USER_EDIT,
            ROUTE_OPERATORS,
            ROUTE_OPERATOR_EDIT,
            ROUTE_TRANSPORTS,
            ROUTE_TRANSPORT_EDIT,
            ROUTE_UNITS,
            ROUTE_SERVICE_HOTELS,
            ROUTE_SERVICE_CIRCUITS,
            ROUTE_SERVICE_DAYTRIPS,
            ROUTE_SERVICE_TOURS,
            ROUTE_SERVICE_INTERNATIONALS,
            ROUTE_SERVICE_LOCKEDS,
            ROUTE_SERVICE_LOCKED_SHOW,
            ROUTE_SERVICE_GROUPS,
            ROUTE_SERVICE_GROUP_SHOW,
            ROUTE_SERVICE_EDIT,
            ROUTE_SERVICE_TRAVEL_DATE,
            ROUTE_SERVICE_EXIT_PLACES,
            ROUTE_SERVICE_EXIT_PLACE_PRICES,
            ROUTE_RESERVATION_HOTEL_INDEX,
            ROUTE_RESERVATION_HOTEL_CREATE,
            ROUTE_RESERVATION_HOTEL_SHOW,
            ROUTE_RESERVATION_HOTEL_EDIT,
            ROUTE_RESERVATION_CIRCUIT_INDEX,
            ROUTE_RESERVATION_CIRCUIT_CREATE,
            ROUTE_RESERVATION_CIRCUIT_SHOW,
            ROUTE_RESERVATION_CIRCUIT_EDIT,
            ROUTE_RESERVATION_DAYTRIPS_INDEX,
            ROUTE_RESERVATION_DAYTRIPS_CREATE,
            ROUTE_RESERVATION_DAYTRIPS_SHOW,
            ROUTE_RESERVATION_DAYTRIPS_EDIT,
            ROUTE_RESERVATION_TOURS_INDEX,
            ROUTE_RESERVATION_TOURS_CREATE,
            ROUTE_RESERVATION_TOURS_SHOW,
            ROUTE_RESERVATION_TOURS_EDIT,
            ROUTE_USER_PERMISSIONS,
            ROUTE_USER_BRANCHOFFICES,
            ROUTE_RESERVATION_INTERNATIONALS_INDEX,
            ROUTE_RESERVATION_INTERNATIONALS_CREATE,
            ROUTE_RESERVATION_INTERNATIONALS_SHOW,
            ROUTE_RESERVATION_INTERNATIONALS_EDIT,
            ROUTE_RESERVATION_FLIGHTS_INDEX,
            ROUTE_RESERVATION_FLIGHTS_CREATE,
            ROUTE_RESERVATION_FLIGHTS_SHOW,
            ROUTE_RESERVATION_FLIGHTS_EDIT,
            ROUTE_RESERVATION_OTHER_SERVICE_INDEX,
            ROUTE_RESERVATION_OTHER_SERVICE_CREATE,
            ROUTE_RESERVATION_OTHER_SERVICE_SHOW,
            ROUTE_RESERVATION_OTHER_SERVICE_EDIT,
            ROUTE_RESERVATION_SELECT_SEAT_INDEX,
            ROUTE_RESERVATION_NOTES,
            ROUTE_PROFILE_LIST,
            ROUTE_PROFILE_ADD,
            ROUTE_PROMOTIONS,
            ROUTE_PROMOTION_ADD,
            ROUTE_PROMOTION_EDIT,
            ROUTE_COUPONS,
            ROUTE_COUPON_ADD,
            ROUTE_BANKS,
            ROUTE_BANK_ADD,
            ROUTE_BANK_EDIT,
            ROUTE_RESERVATION_PAYMENTS,
            ROUTE_RESERVATION_LIST_PAYMENTS,
            ROUTE_RESERVATION_PEOPLE,
            ROUTE_BRANCH_BOXES,
            ROUTE_BOX_HISTORY,
            ROUTE_BOX_HISTORY_MOVE_LIST,
            ROUTE_SAVING,
            ROUTE_BANK_HISTORY_MOVE_LIST,
            ROUTE_RESERVATION_PAYMENT_TO_BROOKER,
            ROUTE_PROMOTION_FOLDER,
            ROUTE_RESERVATION_PACKAGED_INDEX,
            ROUTE_RESERVATION_PACKAGED_CREATE,
            ROUTE_RESERVATION_PACKAGED_SHOW,
            ROUTE_RESERVATION_PACKAGED_EDIT,
            ROUTE_ACTIVITY,
            ROUTE_REPORTS,
            ROUTE_RESERVATION_TRANSFER_INDEX,
            ROUTE_RESERVATION_TRANSFER_CREATE,
            ROUTE_RESERVATION_TRANSFER_SHOW,
            ROUTE_RESERVATION_TRANSFER_EDIT,
            ROUTE_PAYMENT_MULTIPLE,
            ROUTE_PAYMENT_MULTIPLE_ADD,
            ROUTE_SERVICE_QUOTE,
            ROUTE_SERVICE_QUOTE_ADD,
            ROUTE_RESERVATION_LOCKEDS_INDEX,
            ROUTE_RESERVATION_LOCKEDS_CREATE,
            ROUTE_RESERVATION_LOCKEDS_SHOW,
            ROUTE_RESERVATION_LOCKEDS_EDIT,
            ROUTE_RESERVATION_GROUPS_INDEX,
            ROUTE_RESERVATION_GROUPS_CREATE,
            ROUTE_RESERVATION_GROUPS_SHOW,
            ROUTE_RESERVATION_GROUPS_EDIT,
            ROUTE_TRAVEL_GALLERY_INDEX
        };
    },
    computed: {
        ...mapGetters("auth", ["user"]),
        ...mapGetters("config", ["configuration"]),

        currentPath() {
            return this.$route.name;
        },
        collapsed() {
            return isSidebarCollapsed();
        },
        expanded() {
            return !isSidebarEffectivelyCollapsed();
        }
    },
    mounted() {
        this.scrollAreaEl = this.$el.querySelector('.scroll-area');
        if (this.scrollAreaEl) {
            this.scrollAreaEl.addEventListener('scroll', this.handleNavScroll);
        }
    },
    beforeDestroy() {
        if (this.scrollAreaEl) {
            this.scrollAreaEl.removeEventListener('scroll', this.handleNavScroll);
        }
        clearTimeout(this.navScrollHideTimeout);
    },
    methods: {
        ACCESS_MENU,
        ACCESS_MENU_ACTION,
        setSidebarHovering,
        // Keeps the nav's own scrollbar invisible at rest; only reveals it
        // while actively scrolling, same treatment as .page-wrapper.
        handleNavScroll() {
            this.scrollAreaEl.classList.add('is-scrolling');
            clearTimeout(this.navScrollHideTimeout);
            this.navScrollHideTimeout = setTimeout(() => {
                this.scrollAreaEl.classList.remove('is-scrolling');
            }, 800);
        },
        toggleMenu(menu) {
            if (menu != this.menuActive && this.openMenu == true) {
                this.menuActive = menu;
                return;
            }

            this.menuActive = menu;
            this.openMenu = !this.openMenu;
        }
    },
}
</script>

<style scoped>
.scroll-area {
    position: relative;
    margin: auto;
    height: calc(100vh - 180px);
    background-color: transparent !important;
}
</style>